import {skipToken} from '@reduxjs/toolkit/query';

import {isNotNil} from 'ramda';

import {useGetTenantIntegrationsAdvancedSearchQuery} from '@dms/api/core';
import {useGetCurrentBranch, useIsLicenceEnabled, useTenant} from '@dms/shared';

const HYUNDAI_MOBIS_IRIS_INTEGRATION_CODE = 'HYUNDAI_MOBIS_IRIS';
const MERCEDES_MRA_INTEGRATION_CODE = 'MRA_REPORTING';
const DAIMLER_MRA_INTEGRATION_CODE = 'DAIMLER_MRA_REPORTING';
const MERCEDES_5STAR_INTEGRATION_CODE = 'MERC_5STAR';
const MERCEDES_CSTAR_INTEGRATION_CODE = 'MERC_CSTAR';
const HONDA_MONTHLY_REPORT_INTEGRATION_CODE = 'HONDA_MONTHLY_REPORT';

/**
 * Resolves which importer reporting integrations the tenant has enabled.
 * The underlying query fires only for tenants that could ever see importer
 * reports (workshop or warehouse licence), so the check is free for everyone else.
 */
export function useImporterReportingIntegrations() {
  const tenantData = useTenant();
  const {activeBranchId} = useGetCurrentBranch();
  const {isWorkshopModuleEnabled, isWarehouseModuleEnabled} = useIsLicenceEnabled();

  const tenantId = tenantData.tenant?.id;
  const isEntitledToQuery =
    (isWorkshopModuleEnabled || isWarehouseModuleEnabled) && isNotNil(tenantId);

  const tenantIntegrationsQuery = useGetTenantIntegrationsAdvancedSearchQuery(
    isEntitledToQuery ? {tenantId} : skipToken
  );
  const hondaMonthlyReportIntegrationQuery = useGetTenantIntegrationsAdvancedSearchQuery(
    isEntitledToQuery && activeBranchId
      ? {
          tenantId,
          branchId: [activeBranchId],
          integrationCode: [HONDA_MONTHLY_REPORT_INTEGRATION_CODE],
        }
      : skipToken
  );

  const codes = new Set(tenantIntegrationsQuery.data?.map((integration) => integration.code) ?? []);
  const hondaMonthlyReportCodes = new Set(
    hondaMonthlyReportIntegrationQuery.data?.map((integration) => integration.code) ?? []
  );

  return {
    isHyundaiMobisIrisEnabled: codes.has(HYUNDAI_MOBIS_IRIS_INTEGRATION_CODE),
    isMercedesMraEnabled:
      codes.has(MERCEDES_MRA_INTEGRATION_CODE) || codes.has(DAIMLER_MRA_INTEGRATION_CODE),
    isMerc5StarEnabled: codes.has(MERCEDES_5STAR_INTEGRATION_CODE),
    isMercCstarEnabled: codes.has(MERCEDES_CSTAR_INTEGRATION_CODE),
    isHondaMonthlyReportEnabled: hondaMonthlyReportCodes.has(HONDA_MONTHLY_REPORT_INTEGRATION_CODE),
    isHondaMonthlyReportLoading: hondaMonthlyReportIntegrationQuery.isLoading,
    isHondaMonthlyReportError: hondaMonthlyReportIntegrationQuery.isError,
    isLoading: tenantIntegrationsQuery.isLoading,
    isError: tenantIntegrationsQuery.isError,
  };
}
